<script lang="ts">
	import {
		Modal,
		Container,
		Input,
		Button,
		Text,
		ImageGroup,
		ImageUpload,
	} from '@Shelly/shelly-design-system';
	import { get } from 'svelte/store';
	import { profiles, showProfileModal } from 'src/stores/features/profiles';
	import { handleUserProfileName, handleUserProfileAvatar } from 'src/services/http';
	import { setUnitName } from 'src/services/iotu/house-management';
	import { currentUnitId, refreshHomes } from 'src/stores/account/home';
	import { user_data, user_settings } from 'src/stores/account/user';
	import { getImages, mapImgArray, uploadImage } from '../Lists/list-helpers';
	import { load_image_from } from 'src/helpers/interface_helpers';
	import { onDestroy, onMount } from 'svelte';
	import { format } from 'svelte-i18n';
	import type { ImageGroupImageType } from '@Shelly/shelly-design-system';
	import { AvatarColorVariant } from '@Shelly/shelly-design-system/helpers/enums';
	import { imgGroupHousesArr } from './profiles_helpers';
	import { hasOwnedUnit } from 'src/helpers/houses/homes_helpers';

	export let showModal = false;

	let activeProfile = $profiles.find((item) => item.isSelected);
	let profileName = activeProfile?.title || '';
	let selectedImage = '';

	$: canUploadImages = hasOwnedUnit($user_data?.available_units);
	$: disableSave = !profileName || profileName.trim() === '';

	$: allImages = [...imgGroupColors, ...imgGroupHouses, ...uploadedImages];

	const colorValues = Object.values(AvatarColorVariant).slice(0, -5);
	const colorArr = Object.values(colorValues);
	let imgGroupColors: ImageGroupImageType[] = [...Array(colorArr.length).keys()].map((_, i) => {
		const currentColor = colorValues[i] as ImageGroupImageType['backgroundColor'];
		return {
			backgroundColor: currentColor,
			id: i,
			selected: false,
			type: 'color',
		};
	});

	let imgGroupHouses = [...imgGroupHousesArr];

	let uploadedImages: ImageGroupImageType[] = [].map((_, i) => {
		return {
			image: `/images/imageGroup/uploaded-0${i + 1}.jpg`,
			id: i,
			selected: false,
			type: 'uploaded',
		};
	});

	const setActiveProfileSelectedImage = () => {
		if (!activeProfile) return;
		allImages = [...imgGroupColors, ...imgGroupHouses, ...uploadedImages].map((img) => {
			if (
				img.image &&
				activeProfile.acc_avatar.image &&
				img.image === activeProfile.acc_avatar.image
			) {
				return {
					...img,
					selected: true,
				};
			} else if (
				img.backgroundColor &&
				activeProfile.acc_avatar.backgroundColor &&
				img.backgroundColor === activeProfile.acc_avatar.backgroundColor
			) {
				return {
					...img,
					selected: true,
				};
			}

			return {
				...img,
				selected: false,
			};
		});

		if (allImages.filter((img) => img.selected).length > 1) {
			allImages = allImages.map((img) => {
				if (img.type === 'color') img.selected = false;
				return img;
			});
		}

		imgGroupColors = allImages.filter((img) => img.type === 'color');
		imgGroupHouses = allImages.filter((img) => img.type === 'house');
		uploadedImages = allImages.filter((img) => img.type === 'uploaded');
	};

	const onSelectImg = (e: CustomEvent) => {
		const selectedImgID = e.detail.id;
		const selectedType = e.detail.type;

		allImages = allImages.map((img) => {
			if (selectedImgID === img.id && img.type === selectedType) {
				if (img.image) {
					activeProfile.acc_avatar.image = img.image;
					delete activeProfile.acc_avatar.backgroundColor;
				}

				if (img.backgroundColor) {
					activeProfile.acc_avatar.backgroundColor = img.backgroundColor;
					delete activeProfile.acc_avatar.image;
				}

				return {
					...img,
					selected: true,
				};
			}

			return {
				...img,
				selected: false,
			};
		});

		imgGroupColors = allImages.filter((img) => img.type === 'color');
		imgGroupHouses = allImages.filter((img) => img.type === 'house');
		uploadedImages = allImages.filter((img) => img.type === 'uploaded');
	};

	const onUploadImage = async (e) => {
		const customAvatarDB = await uploadImage(e, 'avatar');
		if (Array.isArray(customAvatarDB)) {
			removeActiveImage();

			selectedImage = customAvatarDB[0].filepath;

			const imageUrl = load_image_from(selectedImage);

			uploadedImages = [
				{
					filepath: customAvatarDB[0].filepath,
					image: imageUrl,
					id: 0,
					selected: true,
					type: 'uploaded',
				},
			];

			// Auto-select the uploaded image for activeProfile
			activeProfile.acc_avatar = {
				...activeProfile.acc_avatar,
				image: imageUrl,
			};

			delete activeProfile.acc_avatar.backgroundColor;

			allImages = [...imgGroupColors, ...imgGroupHouses, ...uploadedImages];
			setActiveProfileSelectedImage();
		}

		// to be able to upload same img after that
		e.target.value = '';
	};

	const removeActiveImage = () => {
		allImages = allImages.map((img) => {
			return {
				...img,
				selected: false,
			};
		});

		imgGroupHouses = allImages.filter((img) => img.type === 'house');
	};

	const onCloseModal = () => {
		showProfileModal.set(false);
		if (!$user_settings.acc_avatar && !$user_settings.name) {
			console.log(
				'closed prompt to initially edit profile without editing it. Will override with user_data.',
			);
			handleUserProfileName(activeProfile.title);
			handleUserProfileAvatar(activeProfile.acc_avatar);
		}
	};

	const onProfileNameChange = (e: InputEvent) => {
		const input = e.target as HTMLInputElement;
		profileName = input.value;
	};

	/**
	 * For a brand-new account the cloud auto-names the default home "Unit <id>".
	 * Mirror the account name onto that home so the homes list shows the account
	 * name too. Gated on `new_user` so editing an existing profile never renames
	 * a home the user may have already named.
	 */
	const syncNewAccountHomeName = async (name: string): Promise<void> => {
		const trimmed = name.trim();
		if (!trimmed) return;

		const unitId = Number(get(currentUnitId) || get(user_data).user_id);
		if (!Number.isFinite(unitId) || unitId <= 0) return;

		try {
			const result = await setUnitName(unitId, trimmed);
			if (result?.isok) {
				await refreshHomes();
			}
		} catch (error) {
			console.error('[setup-profile] failed to set default home name', error);
		}
	};

	const onSaveProfile = async () => {
		if (disableSave) return;

		const isNewAccount = !!$user_settings?.new_user;

		if (profileName !== $user_settings.name) {
			await handleUserProfileName(profileName);
			activeProfile.title = profileName;
		}

		if (isNewAccount) {
			await syncNewAccountHomeName(profileName);
		}

		//const avatar = activeProfile.avatar?.image || activeProfile.avatar?.backgroundColor;

		//check if avatar is changed
		if (activeProfile.acc_avatar !== $user_settings.acc_avatar) {
			await handleUserProfileAvatar(activeProfile.acc_avatar);
		}

		profiles.edit(activeProfile);
		onCloseModal();
	};

	onMount(async () => {
		uploadedImages = await getImages('avatar');
	});

	onDestroy(() => {
		selectedImage = '';
	});

	$: (uploadedImages, setUploadedImages());

	const setUploadedImages = () => {
		uploadedImages = mapImgArray(uploadedImages, 'uploaded', selectedImage);
		allImages = [...imgGroupColors, ...imgGroupHouses, ...uploadedImages];
		setActiveProfileSelectedImage();
	};
</script>

<Modal
	on:close={onCloseModal}
	title={$format('title_set_up_profile', { default: 'Edit Profile' })}
	{showModal}
	removeScrollPadding
>
	<div slot="content">
		<Container noScroll padding="scrollPaddings">
			<Text styleType="h2" class="mb-s">
				{$format('label_profile_name', { default: 'Profile name' })}
			</Text>
			<Input
				value={profileName}
				id="setup-profile-name-input"
				aria-label={$format('label_profile_name', { default: 'Profile name' })}
				error={profileName === ''
					? $format('input_error_empty_name', {
							default: 'The name can not be empty!',
						})
					: ''}
				on:input={onProfileNameChange}
			/>
			<hr class="mt-l mb-l" />
		</Container>
		<Container padding="scrollPaddings">
			<Text styleType="h2" class="mb-m">
				{$format('label_select_avatar', { default: 'Select an avatar' })}
			</Text>

			{#if canUploadImages}
				<ImageGroup
					class="mb-m"
					title={$format('title_uploaded_pictures', {
						default: 'Uploaded pictures',
					})}
					images={uploadedImages}
					single={true}
					on:click={onSelectImg}
				>
					<ImageUpload
						slot="imgUpload"
						accept=".jpg, .jpeg, .heif, .png"
						on:change={onUploadImage}
						type={'circle'}
						btnSettings={null}
					/>
				</ImageGroup>

				<ImageGroup
					class="mb-m"
					title={$format('title_default_pictures', {
						default: 'Default pictures',
					})}
					images={imgGroupHouses}
					on:click={onSelectImg}
				/>
			{/if}

			<!-- DO NOT REMOVE COMMENT: Padding references -->
			<ImageGroup
				class="mb-s"
				title={$format('image_group_title_colors_gradients', {
					default: 'Colors and Gradients',
				})}
				images={imgGroupColors}
				on:click={onSelectImg}
			/>
		</Container>
	</div>
	<div class="d-flex justify-content-end" slot="footer">
		<Button
			variant="secondary"
			class="min-width-md-m min-width-sm-auto width-md-auto width-sm-full text-nowrap mr-s"
			responsiveSize={{
				sm: 'm',
			}}
			on:click={onCloseModal}
		>
			{$format('button_cancel', { default: 'Cancel' })}
		</Button>
		<Button
			class="min-width-md-m min-width-sm-auto width-md-auto width-sm-full text-nowrap"
			disabled={allImages.filter((img) => img.selected).length === 0 || disableSave}
			responsiveSize={{
				sm: 'm',
			}}
			on:click={onSaveProfile}
		>
			{$format('button_save', { default: 'Save' })}
		</Button>
	</div>
</Modal>
